import { WrappedFormUtils } from 'antd/lib/form/Form';
import { useCallback, useState } from 'react';
import Swal from 'sweetalert2';
import {
  BezorgmomentResponse,
  CustomerInformationForBezorgmomentenResponse
} from '../services/deliverer/types';
import { getUpsertCartRequest } from '../selectors/Order/selectors';
import { delivererClient } from '../services/deliverer/DelivererClient';
import { FormValuesSap } from '../selectors/Order/types';
import { mapPaymentMethodToApiValue } from '../helpers/payment';

interface UseUpsertCartRequestParams {
  form: WrappedFormUtils;
  customerInformation: CustomerInformationForBezorgmomentenResponse;
  selectedBezorgmoment?: BezorgmomentResponse | null;
  cartId: string | null;
  setCartId: (cartId: string | null) => void;
  voucherCode: string[] | null;
  setVoucherCode: (voucherCode: string[] | null) => void;
}

export const useUpsertCartRequest = ({
  form,
  customerInformation,
  selectedBezorgmoment,
  cartId,
  setCartId,
  voucherCode,
  setVoucherCode
}: UseUpsertCartRequestParams) => {
  const [notifications, setNotifications] = useState<string[]>([]);

  const upsertCart = useCallback(async (): Promise<boolean> => {
    try {
      const values = await new Promise<FormValuesSap>((resolve, reject) => {
        form.validateFields(
          [
            'delivery_date',
            'weeklyMenuItems',
            'alternativeItems',
            'route_comments',
            'voucher_code'
          ],
          (errors, values) => {
            if (errors) {
              console.error('Validation errors:', errors);
              reject(errors);
            } else {
              resolve(values as FormValuesSap);
            }
          }
        );
      });

      const upsertCartRequest = getUpsertCartRequest(
        customerInformation,
        selectedBezorgmoment || ({} as BezorgmomentResponse),
        cartId,
        values
      );

      const response = await delivererClient.upsertCart(upsertCartRequest);
      setCartId(response.cartId);
      setVoucherCode(response.couponCodes);

      if (response.notifications.length) {
        response.notifications.forEach((notification) => {
          setNotifications([notification]);
        });
        // when there are notifications, do not proceed to checkout
        return false;
      } else {
        setNotifications([]); // clear notifications if none are present
      }

      // perform checkout and payment actions
      await delivererClient.checkoutCart(response.cartId);
      await delivererClient.createPaymentForCart(
        response.cartId,
        mapPaymentMethodToApiValue(customerInformation.paymentMethod) || ''
      );

      setCartId(null); // clear the cart ID after checkout
      await Swal({
        title: 'Bestelling opgeslagen',
        text:
          'De bestelling is succesvol opgeslagen. Het kan even duren voordat de bestelling zichtbaar is.',
        type: 'success',
        confirmButtonText: 'OK'
      });
      return true;
    } catch (error) {
      console.error('Error during upsertCart:', error);
      await Swal({
        title: 'Fout',
        text: 'Er is een fout opgetreden bij het opslaan van de bestelling.',
        type: 'error',
        confirmButtonText: 'OK'
      });
      return false;
    }
  }, [form, customerInformation, selectedBezorgmoment, cartId, setCartId, setVoucherCode]);

  return {
    upsertCart,
    notifications,
    cartId,
    setCartId,
    voucherCode,
    setVoucherCode
  };
};
